import React, { useState, useEffect } from 'react';
import { Text, TextVariant } from '@naf/text';
import { Grid, GridCol } from '@naf/grid';
import { Link } from '@naf-simba/elements-links';
import styled from '@emotion/styled';
import { Message } from '@naf/input';
import { apiClient, ApiType } from '@shared/apiClient';
import { DocumentSnapshotsBlockType } from '@appTypes/DocumentSnapshotsBlockType';
import { ArticleReference } from '@appTypes/articleReferenceType';
import { NewsCardLoader } from '@components/skeleton-loaders/NewsCardLoader';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { useAuth0Token } from '@hooks/useAuth0Token';
import { NewsCard } from './News/NewsCard';

export const DocumentSnapshots = ({
  block: {
    data: { internalEndpoint, internalLink, title },
  },
}: {
  block: DocumentSnapshotsBlockType;
}) => {
  const [data, setData] = useState<{ items: ArticleReference[] }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const link = useDocumentUrlWithNode(internalLink);
  const { simpleToken, loading: tokenLoading } = useAuth0Token();

  // Wait for the token to resolve so logged-in users get articles with the correct access level
  useEffect(() => {
    if (!internalEndpoint || tokenLoading) return undefined;

    const controller = new AbortController();
    setIsLoading(true);
    setError(false);

    apiClient
      .get<{ items: ArticleReference[] }>(ApiType.external, {
        url: internalEndpoint,
        skipNafHeaders: false,
        signal: controller.signal,
      })
      .then((response) => setData(response.data))
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [internalEndpoint, tokenLoading, simpleToken]);

  if (error) return <Message error>Klarte ikke å laste sist publiserte artikler</Message>;
  return (
    <div>
      <HeaderWrapper>
        <Text variant={TextVariant.Header2}>{title}</Text>
        {link && <Link tapTarget to={link} text="Se alle" />}
      </HeaderWrapper>
      <Grid>
        {data?.items.map((article) => (
          <NewsCard article={article} key={article.id} />
        ))}
        {!data && isLoading && (
          <>
            <GridCol s="12" m="6" l="6" xl="4">
              <NewsCardLoader />
            </GridCol>
            <GridCol s="12" m="6" l="6" xl="4">
              <NewsCardLoader />
            </GridCol>
            <GridCol s="12" m="6" l="6" xl="4">
              <NewsCardLoader />
            </GridCol>
          </>
        )}
      </Grid>
    </div>
  );
};

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
